import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, FormControl, InputLabel, Select, MenuItem,
  CircularProgress, Button, ButtonGroup, Grid,
} from '@mui/material';
import SettingsIcon from '@mui/icons-material/Settings';
import DateRangeIcon from '@mui/icons-material/DateRange';
import { smartWarehouseService } from '../services/smartWarehouseService';
import dashboardService, {
  DashboardSummary, HourlyData, ThroughputData, ActiveWave, ActivityFeedItem,
} from '../services/dashboardService';
import useWmsPolling from '../shared/useWmsPolling';
import DashboardSummaryCards from './dashboard/DashboardSummaryCards';
import DashboardHourlyChart from './dashboard/DashboardHourlyChart';
import DashboardThroughput from './dashboard/DashboardThroughput';
import DashboardActiveWaves from './dashboard/DashboardActiveWaves';
import DashboardActivityFeed from './dashboard/DashboardActivityFeed';
import DashboardPipeline from './dashboard/DashboardPipeline';
import DashboardWidgetSettings, {
  DashboardWidgetVisibility,
  loadWidgetVisibility,
} from './dashboard/DashboardWidgetSettings';
import { useTranslation } from 'react-i18next';

interface Warehouse { id: string; warehouseName: string; }

const WmsDashboard: React.FC = () => {
  const { t } = useTranslation('warehouse');
  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  const [selectedWarehouse, setSelectedWarehouse] = useState('');
  const [period, setPeriod] = useState('today');
  const [loading, setLoading] = useState(true);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [widgets, setWidgets] = useState<DashboardWidgetVisibility>(() => loadWidgetVisibility());

  const [summary, setSummary] = useState<DashboardSummary | null>(null);
  const [hourly, setHourly] = useState<HourlyData[]>([]);
  const [throughput, setThroughput] = useState<ThroughputData | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const list = await smartWarehouseService.getAllList();
        setWarehouses(list);
        if (list.length > 0) setSelectedWarehouse(list[0].id);
      } catch { /* ignore */ }
      setLoading(false);
    })();
  }, []);

  const fetchDashboard = useCallback(async () => {
    if (!selectedWarehouse) return;
    try {
      const [s, h, th] = await Promise.all([
        dashboardService.getSummary(selectedWarehouse),
        dashboardService.getHourly(selectedWarehouse, period),
        dashboardService.getThroughput(selectedWarehouse, period),
      ]);
      setSummary(s);
      setHourly(Array.isArray(h) ? h : []);
      setThroughput(th);
    } catch { /* ignore */ }
  }, [selectedWarehouse, period]);

  useEffect(() => { fetchDashboard(); }, [fetchDashboard]);

  const { data: activeWaves } = useWmsPolling<ActiveWave[]>(
    () => dashboardService.getActiveWaves(selectedWarehouse),
    10000,
    !!selectedWarehouse && widgets.activeWaves
  );

  const { data: feed } = useWmsPolling<ActivityFeedItem[]>(
    () => dashboardService.getActivityFeed(selectedWarehouse),
    10000,
    !!selectedWarehouse && widgets.activityFeed
  );

  if (loading) {
    return (
      <Box sx={{ p: 3, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  const showChartsRow = widgets.hourlyChart || widgets.throughput || widgets.pipeline;
  const showBottomRow = widgets.activeWaves || widgets.activityFeed;
  const chartCols = [widgets.hourlyChart, widgets.throughput, widgets.pipeline].filter(Boolean).length;
  const mdChart = chartCols === 1 ? 12 : chartCols === 2 ? 6 : 4;

  return (
    <Box sx={{ pb: 4 }}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          justifyContent: 'space-between',
          alignItems: { xs: 'stretch', md: 'center' },
          gap: { xs: 2, md: 0 },
          mb: 2,
          backgroundColor: 'background.paper',
          py: 1,
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Typography variant="h5" sx={{ fontWeight: 700, textAlign: { xs: 'center', md: 'left' } }}>
          {t('pages.dashboard.title')}
        </Typography>

        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            gap: { xs: 1, md: 0.75 },
            alignItems: 'center',
            justifyContent: 'flex-end',
            width: { xs: '100%', md: 'auto' },
          }}
        >
          <FormControl size="small" sx={{ minWidth: 220, width: { xs: '100%', md: 220 } }}>
            <InputLabel>{t('common.warehouse')}</InputLabel>
            <Select
              value={selectedWarehouse}
              onChange={(e) => setSelectedWarehouse(e.target.value)}
              label={t('common.warehouse')}
              sx={{ height: 32 }}
            >
              {warehouses.map((w) => (
                <MenuItem key={w.id} value={w.id}>
                  {w.warehouseName}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Button
            variant="outlined"
            size="small"
            startIcon={<DateRangeIcon />}
            disabled
            title={t('pages.dashboard.dateRangeSoon')}
            sx={{ textTransform: 'none', whiteSpace: 'nowrap', height: 32, px: 1.25 }}
          >
            {t('pages.dashboard.dateRange')}
          </Button>

          <ButtonGroup
            variant="outlined"
            size="small"
            fullWidth
            sx={{
              width: { xs: '100%', md: 'auto' },
              '& .MuiButton-root': { height: 32, px: 1.25, whiteSpace: 'nowrap', minWidth: 62 },
            }}
          >
            {(['today', 'week', 'month'] as const).map((p) => (
              <Button
                key={p}
                variant={period === p ? 'contained' : 'outlined'}
                onClick={() => setPeriod(p)}
                sx={{ textTransform: 'none', flex: 1 }}
              >
                {t(`pages.dashboard.period.${p}`)}
              </Button>
            ))}
          </ButtonGroup>

          <Button
            variant="contained"
            startIcon={<SettingsIcon />}
            onClick={() => setSettingsOpen(true)}
            sx={{
              display: 'inline-flex',
              borderRadius: 2,
              textTransform: 'none',
              height: 32,
              px: 1.5,
              boxShadow: 2,
              '&:hover': { boxShadow: 4 },
              width: { xs: '100%', md: 'auto' },
            }}
          >
            {t('pages.dashboard.widgetSettings')}
          </Button>
        </Box>
      </Box>

      {widgets.summaryCards && <DashboardSummaryCards summary={summary} />}

      {showChartsRow && (
        <Grid container spacing={1} sx={{ mb: 2 }}>
          {widgets.hourlyChart && (
            <Grid item xs={12} md={chartCols === 3 ? 5 : mdChart}>
              <DashboardHourlyChart data={hourly} period={period} />
            </Grid>
          )}
          {widgets.throughput && (
            <Grid item xs={12} md={chartCols === 3 ? 3 : mdChart}>
              <DashboardThroughput data={throughput} />
            </Grid>
          )}
          {widgets.pipeline && (
            <Grid item xs={12} md={chartCols === 3 ? 4 : mdChart}>
              <DashboardPipeline
                pipeline={summary?.pipeline ?? []}
                channels={summary?.channels ?? []}
                awaitingCargo={summary?.awaitingCargo ?? 0}
                cancelledToday={summary?.cancelledToday ?? 0}
              />
            </Grid>
          )}
        </Grid>
      )}

      {showBottomRow && (
        <Grid container spacing={1}>
          {widgets.activeWaves && (
            <Grid item xs={12} md={widgets.activityFeed ? 8 : 12}>
              <DashboardActiveWaves waves={activeWaves || []} />
            </Grid>
          )}
          {widgets.activityFeed && (
            <Grid item xs={12} md={widgets.activeWaves ? 4 : 12}>
              <DashboardActivityFeed items={feed || []} />
            </Grid>
          )}
        </Grid>
      )}

      <DashboardWidgetSettings
        open={settingsOpen}
        value={widgets}
        onChange={setWidgets}
        onClose={() => setSettingsOpen(false)}
      />
    </Box>
  );
};

export default WmsDashboard;
